import { TableCell, TableRow, Tooltip } from "@mui/material";
import ChannelCession from "assets/img/svg/ChannelCession";
import ChannelCollective from "assets/img/svg/ChannelCollective";
import ChannelNumerique from "assets/img/svg/ChannelNumerique";
import ChannelOther from "assets/img/svg/ChannelOther";
import ChannelPhysique from "assets/img/svg/ChannelPhysique";
import classNames from "classnames";
import ModalBox from "components/Modal";
import EditSaleNoteModal from "components/shared/EditSaleNoteModal";
import LastElementTable from "components/shared/LastElementTable";
import PenEditButton from "components/shared/PenEditButton";
import type { LastElementTableAPIData, LastElementTableColumns } from "components/shared/LastElementTable/type";
import type { TOverriddenChannelsData } from "components/TitleFormContainer/types";
import { formatLocaleDateString, formatNumber, getCountryName, isNewDate } from "helpers/utils";
import { useAbility } from "hooks/useAbility";
import { useCalculationBaseColumnLabels } from "hooks/useCalculationBaseColumnLabels";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ISalesByChannels, TPriceSource } from "store/managementStore";
import type { TPeriod } from "store/periodStore";
import { useAppSelector } from "store/store";
import type { TFetchStatus } from "store/types";
import { ChannelKind } from "store/types";

import AmazonIcon from "../../../assets/img/amazon_icon.png";
import GooglePlayIcon from "../../../assets/img/google_play_icon.png";
import IBookIcon from "../../../assets/img/ibooks_icon.png";
import variables from "../../../assets/styles/_variables.module.scss";
import styles from "./style.module.scss";

interface ITitleSalesTable {
    data: ISalesByChannels[];
    dataStatus: TFetchStatus;
    APIData?: LastElementTableAPIData;
    titlePrice: number;
    titleChannelsData: TOverriddenChannelsData[];
    currentPeriod: TPeriod | undefined;
    titleId: number;
}

const TitleSalesTable = ({
    data,
    dataStatus,
    APIData,
    titlePrice,
    titleChannelsData,
    currentPeriod,
    titleId,
}: ITitleSalesTable) => {
    const [t] = useTranslation();
    const ability = useAbility();
    const canUpdateSale = ability.can("update", "Sales");
    const [saleToEdit, setSaleToEdit] = useState<ISalesByChannels | null>(null);

    const { titleSalesStatus } = useAppSelector((state) => state.titles);
    const { namedCustomTurnoverColumns, resolveLabel } = useCalculationBaseColumnLabels();
    const turnoverLabel = resolveLabel("turnover");
    const quantityLabel = resolveLabel("quantity");

    const formatChannelColumn = (source: string, sale: ISalesByChannels) => {
        return (
            <div className={styles["source-column"]}>
                <div style={{ display: "inline-flex" }}>
                    {source?.toLocaleLowerCase().includes("amazon") ? (
                        <img src={AmazonIcon} alt="amazon" className={styles["source-column-logo"]} />
                    ) : source?.toLocaleLowerCase().includes("google") ? (
                        <img src={GooglePlayIcon} alt="google" className={styles["source-column-logo"]} />
                    ) : source?.toLocaleLowerCase().includes("apple") ? (
                        <img src={IBookIcon} alt="google" className={styles["source-column-logo"]} />
                    ) : sale.kind === ChannelKind.PHYSICAL ? (
                        <ChannelPhysique width="15px" height="15px" />
                    ) : sale.kind === ChannelKind.DIGITAL ? (
                        <ChannelNumerique width="15px" height="15px" />
                    ) : sale.kind === ChannelKind.RIGHTS_TRANSFER ? (
                        <ChannelCession width="15px" height="15px" />
                    ) : sale.kind === ChannelKind.COLLECTIVE_MANAGEMENT ? (
                        <ChannelCollective width="15px" height="15px" />
                    ) : (
                        sale.kind === ChannelKind.OTHER && <ChannelOther width="15px" height="15px" />
                    )}
                </div>
                <Tooltip title={sale.name + (source ? `: ${source}` : "")} arrow>
                    <div className={styles["source-column-name"]}>
                        <span className={styles["name-channel"]}>{sale.name}</span>
                        <span className={styles["name-source"]}>{source ?? ""}</span>
                    </div>
                </Tooltip>
            </div>
        );
    };

    /**
     * A sale line names the columns it fills: when it carries no turnover of its own, the revenue is
     * the one the API computed from its copies and the applicable price.
     */
    const formatCAAmount = (sale: ISalesByChannels) => {
        const importedTurnover = !!sale.turnover;
        const computedAmount = !importedTurnover;
        // Both columns filled: the file gave the revenue next to the copies.
        const turnoverAlongsideCopies = importedTurnover && !!sale.quantity;

        return (
            <Tooltip title={computedAmount ? t("Management.SaleDetails.ComputedCATooltip") : turnoverLabel} arrow>
                <span
                    className={classNames({
                        [styles["amount-column"]]: true,
                        [styles["computed-amount"]]: computedAmount,
                        [styles["turnover"]]: turnoverAlongsideCopies,
                    })}
                >
                    {importedTurnover
                        ? formatNumber((sale.turnover ?? 0) / 100, {
                              isCurrency: true,
                          })
                        : formatNumber(sale.computedRevenue / 100, {
                              isCurrency: true,
                              preferredCurrency: sale.overridenPriceCurrency,
                          })}
                </span>
            </Tooltip>
        );
    };

    const getPriceSource = (sourceType: TPriceSource, closedPeriod: boolean) => {
        let tooltip = "";
        switch (sourceType) {
            case "defaultPrice":
                tooltip = t("TitleSalesTable.GeneralTable.DefaultPrice");
                break;
            case "channelPrice":
                tooltip = t("TitleSalesTable.GeneralTable.ChannelPrice");
                break;
            case "countryPrice":
                tooltip = t("TitleSalesTable.GeneralTable.CountryPrice");
                break;
            case "overridenPrice":
                tooltip = t("TitleSalesTable.GeneralTable.OverridenPrice");
        }

        if (closedPeriod) {
            tooltip = tooltip + t("TitleSalesTable.GeneralTable.ClosedPeriod");
        }
        return tooltip;
    };

    const formatPriceColumn = (sale: ISalesByChannels) => {
        let price = formatNumber(titlePrice / 100, { isCurrency: true });

        const priceNotDefault = titleChannelsData.find((channel) => +channel.id! === sale.channelId);
        if (priceNotDefault) {
            price = formatNumber(+priceNotDefault.price / 100, { isCurrency: true });
        }

        if (sale.effectivePrice) {
            price = formatNumber(sale.effectivePrice / 100 / sale.exchangeRate, {
                isCurrency: true,
                preferredCurrency: sale.overridenPriceCurrency,
            });
        }

        if (sale.overridenPrice) {
            price = formatNumber(sale.overridenPrice / 100, {
                isCurrency: true,
                preferredCurrency: sale.overridenPriceCurrency,
            });
        }

        const oldPrice = (currentPeriod?.periodStart ?? new Date()) > new Date(sale.date); // Meaning price on closed period.

        const style =
            sale.priceSource !== "defaultPrice"
                ? { cursor: "pointer", textDecoration: "underline" }
                : { cursor: "pointer", color: "#b3b3b3" };

        return (
            <Tooltip title={getPriceSource(sale.priceSource, oldPrice)} arrow>
                <span style={style}>{price}</span>
            </Tooltip>
        );
    };

    const salesColumns: LastElementTableColumns[] = [
        {
            id: "source",
            label: t("Management.Channel"),
            format: (value, id, currentRow: ISalesByChannels) => formatChannelColumn(value, currentRow),
            align: "left",
            sort: true,
        },
        {
            id: "note",
            label: t("TitleSalesTable.GeneralTable.Note"),
            format: (value) => <span className={styles["name-channel"]}>{value || "-"}</span>,
            align: "left",
            sort: true,
            maxWidth: "150px",
        },
        {
            id: "price",
            label: t("NewTitle.Price"),
            align: "right",
            format: (value, id, currentSale: ISalesByChannels) => formatPriceColumn(currentSale),
            sort: true,
            maxWidth: "70px",
        },
        {
            id: "turnover",
            label: turnoverLabel,
            align: "right",
            format: (value, id, currentRow: ISalesByChannels) => formatCAAmount(currentRow),
            sort: true,
        },
        {
            id: "quantity",
            label: quantityLabel,
            align: "right",
            sort: true,
            format: (value) => (value ? formatNumber(value) : "-"),
        },
        ...namedCustomTurnoverColumns.map(({ column, label }) => ({
            id: column,
            label,
            align: "right" as const,
            sort: true,
            format: (value: number | null) => (value ? formatNumber(value / 100, { isCurrency: true }) : "-"),
        })),
        {
            id: "returnedSales",
            label: t("Management.SalesReturns"),
            align: "right",
            sort: true,
            maxWidth: "70px",
            minWidth: "70px",
            // Returns are counted in copies: a line without quantity has nothing to show.
            format: (value, id, currentRow: ISalesByChannels) => (currentRow.quantity ? value : "-"),
        },
        {
            id: "countryMarket",
            label: t("Management.CountrySale"),
            align: "right",
            format: (value) => getCountryName(value) || "-",
            hidden: data.every((sale) => sale.countryMarket === ""),
        },
        {
            id: "created",
            label: t("Management.DateAdded"),
            align: "right",
            format: (value) => formatLocaleDateString({ date: value, options: { numeric: true } }),
            sort: true,
            maxWidth: "100px",
        },
        {
            id: "date",
            label: t("Management.SaleDate"),
            align: "right",
            format: (value) => formatLocaleDateString({ date: value, options: { numeric: true, timeZone: "UTC" } }),
            sort: true,
        },
        {
            id: "edit",
            label: "",
            align: "right",
            hidden: !canUpdateSale,
            maxWidth: "40px",
            minWidth: "40px",
            format: (_value, _id, currentRow: ISalesByChannels) => (
                <PenEditButton onClick={() => setSaleToEdit(currentRow)} tooltipText={t("EditSaleNote.EditTooltip")} />
            ),
        },
    ];

    const visibleColumnsCount = salesColumns.filter((column) => !column.hidden).length;

    const tHeadStyles = {
        maxWidth: 160,
        width: "fit-content",
        fontWeight: 400,
        fontSize: "12px !important",
        padding: "8px 10px",
        backgroundColor: "#F6F6F6",
        fontFamily: "'Poppins', sans-serif",
        lineHeight: "1rem",
    };

    const tBodyStyles = {
        padding: "10px",
        fontSize: "12px",
    };

    const separatorContent = (sale: any) => (
        <TableRow>
            <TableCell style={{ padding: 0, border: "none" }} colSpan={visibleColumnsCount}>
                <div className="bpstatement-newperiod-separator">
                    <span>{new Date(sale.date).getUTCFullYear()}</span>
                </div>
            </TableCell>
        </TableRow>
    );

    const dataLoading = ["idle", "pending"].includes(dataStatus) || ["idle", "pending"].includes(titleSalesStatus);

    return (
        <>
            {saleToEdit && (
                <ModalBox onClose={() => setSaleToEdit(null)} open mainTitle={t("EditSaleNote.Title")}>
                    <EditSaleNoteModal sale={saleToEdit} onClose={() => setSaleToEdit(null)} />
                </ModalBox>
            )}
            <LastElementTable
                columns={salesColumns}
                data={data}
                max={-1}
                tableHeadStyles={tHeadStyles}
                tableBodyStyles={tBodyStyles}
                dataType="titleSales"
                maxHeight={700}
                dataCount={APIData?.count}
                APIData={APIData}
                isLoading={dataLoading}
                columnsNumber={visibleColumnsCount}
                isNewDate={(row: any, index: number, data: any) => isNewDate(row, index, data, "date")}
                separatorContent={(sale: any) => separatorContent(sale)}
                stickyFooter
            />
        </>
    );
};

export default TitleSalesTable;
